// groupLeaf — VQE-Edit edit-action: convert a standalone WHERE leaf into a
// parenthesised group by wrapping it and joining a new predicate, e.g.
// `a = 1` → `(a = 1 OR b = 2)`. Delegates to SR-Edit `group-leaf` via
// `commitEdit`. Addresses the node DIRECTLY by `path`, so it works on a
// root-level leaf (path `[]`) that has no enclosing group.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/modify/remove
// filter (WHERE / HAVING).

import type {
  BooleanClauseTarget,
  NodePath,
  WhereCombinator,
} from '../../../../sql-resolver/types/structuralEdit';
import { commitEdit, type CommitEditCtx } from '../commitEdit';
import { formatFilterText, type FilterFormValue } from './filterHelpers';

/**
 * Wrap the node at `path` in new parentheses and join `predicate` to it with
 * `combinator`: `<node>` → `(<node> <combinator> <predicate>)`, in the WHERE
 * (default) or HAVING (`clause: 'having'`) tree. The predicate is either
 * literal text or a structured `FilterFormValue` stringified via
 * `formatFilterText` (mirrors `addFilterNode`).
 *
 * Returns without applying when `predicate` resolves to an empty string, or
 * when `path` does not resolve to a node (SR-Edit returns no planned edits).
 */
export async function groupLeaf(
  path: NodePath,
  predicate: string | FilterFormValue,
  combinator: WhereCombinator,
  ctx: CommitEditCtx,
  clause?: BooleanClauseTarget,
): Promise<void> {
  const text = (typeof predicate === 'string' ? predicate : formatFilterText(predicate)).trim();
  if (text.length === 0) return;
  await commitEdit({ op: 'group-leaf', path, text, combinator, clause }, ctx);
}
